import {
  Box,
  FormLabel,
  makeStyles,
  Switch,
  Table,
  TableBody,
  TableHead,
} from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ResultsTableRow } from '../../common/ResultsTableRow'
import { Section } from '../../common/Section'
import {
  emissionPerAreaUnit,
  largeEmissionPerYearUnit,
  largeEmissionUnit,
  formatLargeEmission,
  formatEmissionPerArea,
  formatLargeEmissionPerYear,
} from '../../../util/formatters'
import { purchasedEnergySubCategories } from '../../../provider/result-categories'
import { CalculatedVersion } from '../../../calculators/use-calculator'
import { EmissionModel } from '../../../emission-coefficients/emission-coefficients'
import { ResultsTableHeaderRow } from '../../common/ResultsTableHeaderRow'
import { EmissionType } from '../../../generated/graphql'
import { useCarbonFootprintResults } from './use-carbon-footprint-results'

type Props = {
  versions: CalculatedVersion[]
  emissionModel: EmissionModel
  selectedBuildingName: string | null
  containerIdsToNames: Record<string, string>
}

const useStyles = makeStyles((theme) => ({
  toggle: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
}))

export function TotalCarbonFootprintTable({
  versions,
  emissionModel,
  selectedBuildingName,
  containerIdsToNames,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const [firstYearEmissionsOpen, setFirstYearEmissionsOpen] = useState(false)
  const [inspectionPeriodEmissionsOpen, setInspectionPeriodEmissionsOpen] =
    useState(false)
  const [showInherentValues, setShowInherentValues] = useState(false)

  const selectedBuildingIds = selectedBuildingName
    ? Object.entries(containerIdsToNames)
        .filter(([_id, name]) => name === selectedBuildingName)
        .map(([id]) => id)
    : undefined

  const {
    getFirstYearResults,
    getInherentFirstYearResults,
    getCumulativeResults,
    getInherentCumulativeResults,
    getLifetimeFootprints,
    getInherentLifetimeFootprints,
  } = useCarbonFootprintResults(versions, emissionModel, selectedBuildingIds)

  const getFirstYearResultsInternal = showInherentValues
    ? getInherentFirstYearResults
    : getFirstYearResults

  const getCumulativeResultsInternal = showInherentValues
    ? getInherentCumulativeResults
    : getCumulativeResults

  const getLifetimeFootprintsInternal = showInherentValues
    ? getInherentLifetimeFootprints
    : getLifetimeFootprints

  const firstYearUnit = showInherentValues
    ? emissionPerAreaUnit
    : largeEmissionPerYearUnit
  const firstYearFormatter = showInherentValues
    ? formatEmissionPerArea
    : formatLargeEmissionPerYear

  const inspectionPeriodUnit = showInherentValues
    ? emissionPerAreaUnit
    : largeEmissionPerYearUnit
  const inspectionPeriodFormatter = showInherentValues
    ? formatEmissionPerArea
    : formatLargeEmission

  const lifetimeUnit = showInherentValues
    ? emissionPerAreaUnit
    : largeEmissionUnit
  const lifetimeFormatter = showInherentValues
    ? formatEmissionPerArea
    : formatLargeEmission

  return (
    <Section>
      <Box className={classes.toggle}>
        <FormLabel>{t('carbonFootprint.table.showInherentValues')}</FormLabel>
        <Switch
          checked={showInherentValues}
          onChange={() => setShowInherentValues(!showInherentValues)}
        />
      </Box>

      <Table>
        <TableHead>
          <ResultsTableHeaderRow
            open={firstYearEmissionsOpen}
            setOpen={setFirstYearEmissionsOpen}
            title={t('carbonFootprint.table.firstYearEmissions')}
            values={versions.map((version) => version.name)}
          />
        </TableHead>
        <TableBody>
          {purchasedEnergySubCategories.map((subCategory) => (
            <ResultsTableRow
              key={subCategory.nameKey}
              title={`${t(subCategory.nameKey)}, ${firstYearUnit}`}
              values={getFirstYearResultsInternal([subCategory])}
              formatter={firstYearFormatter}
              hide={!firstYearEmissionsOpen}
              hideIfValuesIrrelevant
            />
          ))}
          <ResultsTableRow
            title={`${t('common.total')}, ${firstYearUnit}`}
            values={getFirstYearResultsInternal(purchasedEnergySubCategories)}
            formatter={firstYearFormatter}
            highlight
          />
        </TableBody>

        <TableHead>
          <ResultsTableHeaderRow
            open={inspectionPeriodEmissionsOpen}
            setOpen={setInspectionPeriodEmissionsOpen}
            title={t('carbonFootprint.table.inspectionPeriodEmissions')}
            values={versions.map((version) => version.name)}
          />
        </TableHead>
        <TableBody>
          {purchasedEnergySubCategories.map((subCategory) => (
            <ResultsTableRow
              key={subCategory.nameKey}
              title={`${t(subCategory.nameKey)}, ${inspectionPeriodUnit}`}
              values={getCumulativeResultsInternal([subCategory])}
              formatter={inspectionPeriodFormatter}
              hide={!inspectionPeriodEmissionsOpen}
              hideIfValuesIrrelevant
            />
          ))}
          <ResultsTableRow
            title={`${t('common.total')}, ${inspectionPeriodUnit}`}
            values={getCumulativeResultsInternal(purchasedEnergySubCategories)}
            formatter={inspectionPeriodFormatter}
            highlight
          />
        </TableBody>
        <TableHead>
          <ResultsTableHeaderRow
            title={t('carbonFootprint.table.lifetimeFootprint')}
            values={versions.map((version) => version.name)}
          />
        </TableHead>
        <TableBody>
          {Object.values(EmissionType).map((type) => (
            <ResultsTableRow
              key={type}
              title={`${t(
                `isoCategoryBy.emissionType.${type}`,
              )}, ${lifetimeUnit}`}
              values={getLifetimeFootprintsInternal(type)}
              formatter={lifetimeFormatter}
            />
          ))}
          <ResultsTableRow
            title={`${t('common.total')}, ${lifetimeUnit}`}
            values={getLifetimeFootprintsInternal()}
            formatter={lifetimeFormatter}
            highlight
          />
        </TableBody>
      </Table>
    </Section>
  )
}
